import React from 'react';
import { Facebook, Instagram, Youtube, Twitter, Video, CheckCircle, Award, Users, Target, Clock, Eye, Zap, TrendingUp, AlertCircle, ArrowRight, Star, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';

const PlatformsPage = () => {
  const navigate = useNavigate();

  const platforms = [
    {
      name: "Meta (Facebook & Instagram)",
      icon: Facebook,
      color: "from-blue-500 to-blue-600",
      bgColor: "from-blue-50 to-blue-100",
      bestPractices: [
        {
          title: "3-Second Hook Rule",
          description: "Meta's algorithm prioritizes watch time. ArcRefinery evaluates your first 3 seconds to ensure maximum retention."
        },
        {
          title: "Square & Vertical Formats",
          description: "We analyze composition for 1:1 and 9:16 ratios to maximize feed real estate and engagement."
        },
        {
          title: "Sound-Off Optimization",
          description: "85% of Facebook videos are watched without sound. We check for visual storytelling and caption effectiveness."
        },
        {
          title: "CTA Placement",
          description: "Meta best practices require early CTAs. We verify your call-to-action appears within the first 10 seconds."
        }
      ],
      stats: { engagement: "2.5x", metric: "higher engagement with proper hook", source: "Meta Business" }
    },
    {
      name: "TikTok",
      icon: Video,
      color: "from-pink-500 to-red-500",
      bgColor: "from-pink-50 to-red-100",
      bestPractices: [
        {
          title: "Native Feel Required",
          description: "TikTok punishes overly polished ads. ArcRefinery ensures your content feels authentic and user-generated."
        },
        {
          title: "Trend Integration",
          description: "We analyze whether your video leverages trending sounds, effects, and formats that drive TikTok engagement."
        },
        {
          title: "Fast-Paced Editing",
          description: "TikTok audiences expect quick cuts. We evaluate pacing to ensure you maintain platform-appropriate energy."
        },
        {
          title: "Creator-First Approach",
          description: "The platform favors creator-led content. We assess if your video follows TikTok's creator collaboration guidelines."
        }
      ],
      stats: { engagement: "1.5sec", metric: "average hook time for successful TikTok ads", source: "TikTok for Business" }
    },
    {
      name: "Instagram Reels",
      icon: Instagram,
      color: "from-purple-500 to-pink-500",
      bgColor: "from-purple-50 to-pink-100",
      bestPractices: [
        {
          title: "Vertical-First Design",
          description: "Reels must be 9:16 vertical. We verify your composition maximizes the full-screen mobile experience."
        },
        {
          title: "Music & Audio Strategy",
          description: "Instagram prioritizes videos using trending audio. ArcRefinery checks for audio optimization and trending sound usage."
        },
        {
          title: "Text Overlay Best Practices",
          description: "We analyze text placement, readability, and timing to ensure compliance with Instagram's recommended zones."
        },
        {
          title: "Hashtag & Caption Sync",
          description: "Reels perform better when video content aligns with captions. We evaluate message consistency."
        }
      ],
      stats: { engagement: "22%", metric: "higher reach with platform-optimized Reels", source: "Instagram Insights" }
    },
    {
      name: "YouTube",
      icon: Youtube,
      color: "from-red-500 to-red-600",
      bgColor: "from-red-50 to-red-100",
      bestPractices: [
        {
          title: "Thumbnail Synergy",
          description: "Your first frame should work as a thumbnail. ArcRefinery evaluates opening shot impact and clickability."
        },
        {
          title: "Longer-Form Storytelling",
          description: "YouTube rewards watch time. We assess if your story structure supports 30-60 second engagement minimum."
        },
        {
          title: "Pattern Interrupts",
          description: "YouTube viewers scroll fast. We check for visual pattern interrupts every 5-7 seconds to maintain attention."
        },
        {
          title: "End Screen CTAs",
          description: "YouTube allows clickable end screens. We verify your CTA positioning works with YouTube's interactive elements."
        }
      ],
      stats: { engagement: "60%", metric: "of YouTube ad success depends on first 5 seconds", source: "Google/YouTube" }
    },
    {
      name: "Twitter/X",
      icon: Twitter,
      color: "from-blue-400 to-blue-500",
      bgColor: "from-blue-50 to-blue-100",
      bestPractices: [
        {
          title: "Conversation-Starting Content",
          description: "Twitter thrives on discourse. ArcRefinery checks if your video prompts engagement and discussion."
        },
        {
          title: "Brevity & Impact",
          description: "Twitter users want quick value. We ensure your message is delivered in under 30 seconds with maximum impact."
        },
        {
          title: "Text-Video Harmony",
          description: "Twitter posts combine video + text. We verify your video complements (not repeats) the tweet copy."
        },
        {
          title: "Auto-Play Optimization",
          description: "Twitter auto-plays muted. We check for strong visual hooks that work without audio in timeline view."
        }
      ],
      stats: { engagement: "2.5x", metric: "more engagement with video vs. text-only tweets", source: "Twitter Business" }
    }
  ];

  const howWeHelp = [
    {
      team: "Brands",
      icon: Award,
      color: "from-blue-500 to-indigo-500",
      challenges: [
        "Different creative specs for each platform",
        "Platform-specific algorithm requirements",
        "Ensuring brand consistency across formats"
      ],
      solutions: [
        "Platform-specific scoring for each video",
        "Automated checks against each platform's best practices",
        "Recommendations for cross-platform optimization"
      ]
    },
    {
      team: "Agencies",
      icon: Users,
      color: "from-purple-500 to-pink-500",
      challenges: [
        "Managing multiple client campaigns across platforms",
        "Keeping up with constantly changing platform algorithms",
        "Proving ROI for platform-specific creative"
      ],
      solutions: [
        "Multi-platform analysis in one upload",
        "Up-to-date algorithm compliance checking",
        "Data-backed platform performance predictions"
      ]
    },
    {
      team: "Creators",
      icon: Target,
      color: "from-orange-500 to-red-500",
      challenges: [
        "Understanding what makes content work on each platform",
        "Adapting organic content for paid performance",
        "Learning platform-specific best practices"
      ],
      solutions: [
        "Clear direction on platform requirements",
        "Guidance on optimizing for paid vs. organic",
        "Education on each platform's success factors"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Star className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Multi-Platform Optimization</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Master Every Platform's
              <span className="block mt-2 bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 to-orange-200">
                Best Practices
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              What works on TikTok fails on YouTube. What wins on Instagram flops on Facebook. ArcRefinery ensures your videos excel everywhere.
            </p>
          </div>
        </div>
      </section>

      {/* The Platform Problem */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Platform-Specific Analysis Matters
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Each platform has unique algorithms, user behaviors, and best practices. Generic video analysis misses critical platform-specific optimizations.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-red-50 rounded-xl p-6 border border-red-200">
              <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
              <h3 className="font-bold text-gray-900 mb-3">The Problem</h3>
              <p className="text-gray-700 text-sm mb-3">
                Brands waste millions creating "one-size-fits-all" video ads that violate platform-specific best practices.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✗</span>
                  <span>Wrong aspect ratios for each feed</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✗</span>
                  <span>Ignoring platform algorithm preferences</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✗</span>
                  <span>Missing platform-specific engagement triggers</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <Eye className="w-12 h-12 text-blue-500 mb-4" />
              <h3 className="font-bold text-gray-900 mb-3">The Impact</h3>
              <p className="text-gray-700 text-sm mb-3">
                Platform algorithm penalties, lower reach, higher CPMs, and wasted ad spend on non-optimized creative.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">⚠</span>
                  <span>50-70% lower reach on mismatched content</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">⚠</span>
                  <span>3x higher CPMs for non-native formats</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">⚠</span>
                  <span>Poor performance despite good creative</span>
                </li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-xl p-6 border border-green-200">
              <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
              <h3 className="font-bold text-gray-900 mb-3">The Solution</h3>
              <p className="text-gray-700 text-sm mb-3">
                ArcRefinery analyzes your video against each platform's specific requirements and best practices.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Platform-specific optimization scores</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Algorithm-friendly creative validation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Format and pacing recommendations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Platform-Specific Best Practices */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-indigo-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Platform-Specific Best Practices We Check
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Each platform has unique requirements. We ensure your videos excel on every channel.
            </p>
          </div>

          <div className="space-y-8">
            {platforms.map((platform, index) => {
              const Icon = platform.icon;
              return (
                <div key={index} className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                  <div className={`bg-gradient-to-r ${platform.color} p-6 text-white`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4">
                          <Icon className="w-8 h-8" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold">{platform.name}</h3>
                          <p className="text-white/90 text-sm">Platform-specific analysis & optimization</p>
                        </div>
                      </div>
                      <div className="hidden md:block text-right">
                        <div className="text-3xl font-bold">{platform.stats.engagement}</div>
                        <p className="text-xs text-white/80">{platform.stats.metric}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      {platform.bestPractices.map((practice, idx) => (
                        <div key={idx} className={`bg-gradient-to-br ${platform.bgColor} rounded-lg p-4 border border-gray-200`}>
                          <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                            {practice.title}
                          </h4>
                          <p className="text-gray-700 text-sm">{practice.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How We Help Different Teams */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How ArcRefinery Helps Your Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored platform guidance for brands, agencies, and creators.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {howWeHelp.map((team, index) => {
              const Icon = team.icon;
              return (
                <div key={index} className="bg-white rounded-xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-200">
                  <div className={`bg-gradient-to-r ${team.color} p-6 text-white`}>
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">For {team.team}</h3>
                  </div>

                  <div className="p-6">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                      Your Challenges
                    </h4>
                    <ul className="space-y-2 mb-6">
                      {team.challenges.map((challenge, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start">
                          <span className="text-red-500 mr-2 flex-shrink-0">•</span>
                          <span>{challenge}</span>
                        </li>
                      ))}
                    </ul>

                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      How ArcRefinery Helps
                    </h4>
                    <ul className="space-y-2">
                      {team.solutions.map((solution, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start">
                          <ArrowRight className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span>{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Analyze Per Platform */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What We Analyze for Each Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive platform-specific evaluation across multiple dimensions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-indigo-100">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mb-4">
                <Play className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-3">Format & Specs</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Aspect ratio optimization</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Video length guidelines</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Resolution requirements</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-indigo-100">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-3">Pacing & Timing</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Platform-specific hook timing</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Edit tempo requirements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Optimal video duration</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-indigo-100">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-3">Engagement Tactics</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Platform-native features</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Sound-on vs sound-off</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Text overlay compliance</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-indigo-100">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-teal-500 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-3">Algorithm Alignment</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Watch time optimization</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Completion rate factors</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Engagement signal triggers</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Key Statistics */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              The Platform Optimization Advantage
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center border border-white/20">
              <div className="text-5xl md:text-6xl font-bold mb-2">5+</div>
              <p className="text-indigo-100">Major platforms analyzed simultaneously</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center border border-white/20">
              <div className="text-5xl md:text-6xl font-bold mb-2">50+</div>
              <p className="text-indigo-100">Platform-specific best practices checked per video</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center border border-white/20">
              <div className="text-5xl md:text-6xl font-bold mb-2">3x</div>
              <p className="text-indigo-100">Better performance with platform-optimized creative</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Stop Guessing What Works on Each Platform
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Get platform-specific analysis for Meta, TikTok, Instagram, YouTube, and Twitter—all in one upload.
          </p>
          <button 
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:scale-105"
          >
            Analyse Your Video Now
          </button>
          <p className="text-sm text-gray-500 mt-6">
            No credit card required • Multi-platform analysis • Instant results
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PlatformsPage;

